/**
 * AUTH PROVIDERS - DO NOT MODIFY WITHOUT READING
 *
 * Supports Okta (lens.jamfconcepts.io), basic auth (lensdemo, pilot tenants)
 * and passcode (legacy lensdemo). The runtime config (OKTA_ENABLED /
 * BASIC_AUTH_ENABLED / PASSCODE_ENABLED) determines which provider is active,
 * and must match the selection in main.tsx.
 *
 * See frontend/CLAUDE.md "Authentication" section for header requirements.
 */
import { useOktaAuth } from '@okta/okta-react'
import { useCallback, useEffect } from 'react'
import { getPasscodeToken, PASSCODE_TOKEN_KEY } from '../components/PasscodeScreen'
import { getBasicAuthToken, BASIC_AUTH_TOKEN_KEY } from '../components/BasicLoginScreen'
import { setLoggedOut } from './logoutState'

// Check auth configuration (runtime or build-time config)
const runtimeConfig = (window as { __RUNTIME_CONFIG__?: Record<string, unknown> }).__RUNTIME_CONFIG__ || {}

// Okta config
const oktaIssuer = (runtimeConfig.OKTA_ISSUER as string) || import.meta.env.VITE_OKTA_ISSUER || ''
const oktaClientId = (runtimeConfig.OKTA_CLIENT_ID as string) || import.meta.env.VITE_OKTA_CLIENT_ID || ''
// An explicit false flag from runtime config is authoritative — a configured
// issuer/client-id must not force a provider back on once an operator disabled it.
// Must match the selection logic in main.tsx, or the app renders one provider's
// context while this hook subscribes to another (which hangs with no provider).
const oktaExplicitlyOff = runtimeConfig.OKTA_ENABLED === false || runtimeConfig.OKTA_ENABLED === 'false'
const oktaEnabled = !oktaExplicitlyOff && (runtimeConfig.OKTA_ENABLED === true || runtimeConfig.OKTA_ENABLED === 'true' || (!!oktaIssuer && !!oktaClientId))

// Passcode config (lensdemo only) — only active when Okta is disabled.
// Must match the selection logic in main.tsx so this hook and the provider agree.
const passcodeEnabled = (runtimeConfig.PASSCODE_ENABLED === true || runtimeConfig.PASSCODE_ENABLED === 'true') && !oktaEnabled

// Basic-auth config (lensdemo, pilot tenants) — only active when Okta/Passcode are both disabled.
// Must match the selection logic in main.tsx so this hook and the provider agree.
const basicAuthEnabled = (runtimeConfig.BASIC_AUTH_ENABLED === true || runtimeConfig.BASIC_AUTH_ENABLED === 'true') && !oktaEnabled && !passcodeEnabled

export interface AuthUser {
  // Core identifiers
  sub?: string
  name?: string
  email?: string
  email_verified?: boolean
  picture?: string
  nickname?: string

  // Organization/tenant (for multi-tenancy - may be empty for single-tenant Okta)
  org_id?: string

  // Standard OIDC locale/timezone
  locale?: string
  zoneinfo?: string

  // Jamf-specific claims (from Okta custom claims or empty for basic Okta)
  jamf?: {
    language?: string
    timezone?: string
    theme?: string
    first_name?: string
    last_name?: string
    full_name?: string
    username?: string
    account_id?: string
    contact_id?: string
    is_jamf_employee?: boolean
    idp_groups?: string[]
    connection_name?: string
  }

  // Timestamps
  updated_at?: string
}

export interface LoginOptions {
  returnTo?: string
}

export interface AuthState {
  isLoading: boolean
  isAuthenticated: boolean
  isEnabled: boolean
  authProvider: 'okta' | 'passcode' | 'basic' | 'none'
  user: AuthUser | null
  loginWithRedirect: (options?: LoginOptions) => void
  logout: () => void
  getAccessToken: () => Promise<string | null>
  getIdToken: () => Promise<string | null>
}

/**
 * Unified auth hook over the configured provider (Okta, basic auth or passcode).
 * When none is configured, returns a "logged out" state without errors.
 */
export function useAuth(): AuthState {
  // Passcode mode (lensdemo): the session token in localStorage is the
  // credential. Report enabled/authenticated so PermissionsContext fetches
  // /access/me (real role governs the UI) instead of falling back to the
  // dev-admin or logged-out path.
  if (passcodeEnabled) {
    return usePasscodeHook()
  }

  if (basicAuthEnabled) {
    return useBasicAuthHook()
  }

  // If no auth is configured, return disabled state
  if (!oktaEnabled) {
    return {
      isLoading: false,
      isAuthenticated: false,
      isEnabled: false,
      authProvider: 'none',
      user: null,
      loginWithRedirect: (_options?: LoginOptions) => {
        console.warn('No auth configured. Set OKTA_*, BASIC_AUTH_ENABLED or PASSCODE_ENABLED.')
      },
      logout: () => {},
      getAccessToken: async () => null,
      getIdToken: async () => null,
    }
  }

  // Use Okta if enabled
  return useOktaHook()
}

/**
 * Passcode implementation of the auth hook (lensdemo).
 *
 * There is no IDP here — the HS256 session token issued by /auth/passcode and
 * stored in localStorage is the credential (api.ts sends it as a Bearer token).
 * The synthetic user exists only so consumers that gate on `user` (Sidebar,
 * PermissionsContext's effect deps) behave as authenticated; the real identity
 * and role come from /access/me.
 */
function usePasscodeHook(): AuthState {
  const logout = useCallback(() => {
    setLoggedOut()
    localStorage.removeItem(PASSCODE_TOKEN_KEY)
    // Reload so the app remounts with no token; PasscodeGate reads the
    // logged-out flag (which survives the reload in sessionStorage) and shows
    // the LoggedOut landing instead of the passcode prompt. There is no IdP
    // here, so a reload cannot trigger the SSO auto-relogin loop.
    window.location.reload()
  }, [])

  return {
    isLoading: false,
    isAuthenticated: !!getPasscodeToken(),
    isEnabled: true,
    authProvider: 'passcode',
    user: {
      sub: 'passcode',
      email: 'passcode@lensdemo',
      name: 'Demo User',
    },
    loginWithRedirect: () => {},
    logout,
    getAccessToken: async () => getPasscodeToken(),
    getIdToken: async () => null,
  }
}

/**
 * Basic-auth implementation of the auth hook (pilot tenants e.g. zoomlens).
 *
 * Same pattern as usePasscodeHook: the HS256 session token from /auth/basic/login
 * stored in localStorage is the credential. On 401, clear the token and reload
 * so the app returns to BasicLoginScreen instead of being stuck with no feedback.
 */
function useBasicAuthHook(): AuthState {
  const logout = useCallback(() => {
    setLoggedOut()
    localStorage.removeItem(BASIC_AUTH_TOKEN_KEY)
    // Reload so the app remounts with no token; BasicAuthGate reads the
    // logged-out flag (which survives the reload in sessionStorage) and shows
    // the LoggedOut landing instead of the basic-auth prompt.
    window.location.reload()
  }, [])

  return {
    isLoading: false,
    isAuthenticated: !!getBasicAuthToken(),
    isEnabled: true,
    authProvider: 'basic',
    user: {
      sub: 'basic',
      email: 'pilot@zoomlens',
      name: 'Pilot User',
    },
    loginWithRedirect: () => {
      localStorage.removeItem(BASIC_AUTH_TOKEN_KEY)
      window.location.reload()
    },
    logout,
    getAccessToken: async () => getBasicAuthToken(),
    getIdToken: async () => null,
  }
}

/**
 * Okta implementation of the auth hook
 */
function useOktaHook(): AuthState {
  const { oktaAuth, authState } = useOktaAuth()

  // Debug: Log full Okta auth state and token claims (dev only)
  useEffect(() => {
    if (import.meta.env.DEV && authState?.idToken?.claims) {
      const claims = authState.idToken.claims
      console.group('[Okta] Token Debug Info')
      console.log('[Okta] Auth state:', { isAuthenticated: authState.isAuthenticated, isPending: authState.isPending })
      console.log('[Okta] ID token claims:', claims)
      console.log('[Okta] All claim keys:', Object.keys(claims))

      // Explicit check for groups claim (standard Okta claim name)
      if (claims.groups) {
        console.log('[Okta] ✅ Groups claim found:', claims.groups)
      } else {
        console.warn('[Okta] ⚠️ No "groups" claim in ID token. Ensure:')
        console.warn('  1. The "groups" scope is requested (check main.tsx OktaAuth config)')
        console.warn('  2. The Okta app has Groups claim configured:')
        console.warn('     → Okta Admin → Applications → Your App → Sign On tab')
        console.warn('     → OpenID Connect ID Token → Groups claim type: Filter')
        console.warn('     → Groups claim filter: Regex ".*" to match all groups')
      }

      // Log any potential group claims (common patterns)
      const groupKeys = Object.keys(claims).filter(k =>
        k.includes('group') || k.includes('role') || k.includes('permission') ||
        k.includes('org') || k.startsWith('https://')
      )
      if (groupKeys.length > 0) {
        console.log('[Okta] Potential group/role claims found:')
        groupKeys.forEach(k => console.log(`  ${k}:`, claims[k]))
      }

      // Log Jamf-specific claims if present
      const jamfKeys = Object.keys(claims).filter(k => k.includes('jamf'))
      if (jamfKeys.length > 0) {
        console.log('[Okta] Jamf-specific claims:')
        jamfKeys.forEach(k => console.log(`  ${k}:`, claims[k]))
      }
      console.groupEnd()

      // Also log access token info (without the actual token)
      try {
        const token = oktaAuth.getAccessToken()
        if (token) {
          const parts = token.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]))
            console.group('[Okta] Access Token Debug Info')
            console.log('[Okta] Access token claims:', payload)
            console.log('[Okta] Access token scopes:', payload.scp || payload.scope)
            // Check if groups scope was granted
            const scopes = payload.scp || payload.scope || []
            const scopeArray = Array.isArray(scopes) ? scopes : scopes.split(' ')
            if (scopeArray.includes('groups')) {
              console.log('[Okta] ✅ "groups" scope was granted')
            } else {
              console.warn('[Okta] ⚠️ "groups" scope not in access token. Granted scopes:', scopeArray)
            }
            // Check for groups in access token (custom auth servers may include them here)
            if (payload.groups) {
              console.log('[Okta] Groups in access token:', payload.groups)
            }
            console.groupEnd()
          }
        }
      } catch (e) {
        console.warn('[Okta] Could not decode access token:', e)
      }
    }
  }, [authState, oktaAuth])

  const loginWithRedirect = useCallback((options?: LoginOptions) => {
    oktaAuth.signInWithRedirect({
      originalUri: options?.returnTo || window.location.pathname + window.location.search,
    })
  }, [oktaAuth])

  const logout = useCallback(() => {
    setLoggedOut()
    // Clear only the local Okta tokens; do NOT call signOut() (which redirects
    // to Okta's end-session endpoint and would re-federate). The logged-out flag
    // lets RequireAuth render the landing page instead of auto-relogging-in.
    oktaAuth.tokenManager.clear()
    oktaAuth.authStateManager.updateAuthState()
  }, [oktaAuth])

  const getAccessToken = useCallback(async () => {
    try {
      const token = await oktaAuth.getAccessToken()
      return token || null
    } catch {
      console.warn('Failed to get Okta access token')
      return null
    }
  }, [oktaAuth])

  const getIdToken = useCallback(async () => {
    try {
      const token = await oktaAuth.getIdToken()
      return token || null
    } catch {
      console.warn('Failed to get Okta ID token')
      return null
    }
  }, [oktaAuth])

  // Build user object from ID token claims
  const claims = authState?.idToken?.claims
  const user: AuthUser | null = claims ? {
    sub: claims.sub as string,
    email: (claims.email as string) || (claims.sub as string),
    name: claims.name as string | undefined,
    email_verified: claims.email_verified as boolean | undefined,
    picture: claims.picture as string | undefined,
    nickname: claims.nickname as string | undefined,
    locale: claims.locale as string | undefined,
    zoneinfo: claims.zoneinfo as string | undefined,
    updated_at: claims.updated_at as string | undefined,
    // For single-tenant Okta, org_id may not be present
    org_id: claims.org_id as string | undefined,
    // Jamf-specific claims (if configured in Okta)
    jamf: {
      language: claims['https://jamf.com/language'] as string | undefined,
      timezone: claims['https://jamf.com/timezone'] as string | undefined,
      theme: claims['https://jamf.com/theme'] as string | undefined,
      first_name: claims['https://jamf.com/first_name'] as string | undefined,
      last_name: claims['https://jamf.com/last_name'] as string | undefined,
      full_name: claims['https://jamf.com/full_name'] as string | undefined,
      username: claims['https://jamf.com/username'] as string | undefined,
      account_id: claims['https://jamf.com/account_id'] as string | undefined,
      contact_id: claims['https://jamf.com/contact_id'] as string | undefined,
      is_jamf_employee: claims['https://jamf.com/is_jamf_employee'] as boolean | undefined,
      // Okta uses standard 'groups' claim (requires Groups claim filter in Okta app)
      idp_groups: claims.groups as string[] | undefined,
      connection_name: claims['https://jamf.com/connection_name'] as string | undefined,
    },
  } : null

  return {
    isLoading: !authState,
    isAuthenticated: !!authState?.isAuthenticated,
    isEnabled: true,
    authProvider: 'okta',
    user,
    loginWithRedirect,
    logout,
    getAccessToken,
    getIdToken,
  }
}
